/**
 * The diff between two content versions, inline.
 *
 * Used where something just changed the article and the honest thing to show
 * is what changed — the Ground panel after a grounding revision. Saying "2
 * sections were rewritten" tells you a fact; this tells you whether you want
 * to keep it.
 *
 * Collapsed by default: a grounding run that revised nothing shouldn't put a
 * diff frame on the screen, and a run that did shouldn't shove a wall of
 * markdown in front of the flags that explain why.
 */
import { useEffect, useMemo, useState } from 'react';
import { Diff, Hunk, parseDiff } from 'react-diff-view';
import 'react-diff-view/style/index.css';
import { Link } from 'react-router-dom';
import { withReturnTo } from './common/returnPath';
import api from '../services/api';
import { createGitDiff } from '../utils/contentDiff';

export default function VersionDiff({
  pageId,
  fromVersionId,
  toVersionId,
  label,
}: {
  pageId: number;
  fromVersionId: number;
  toVersionId: number;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [sides, setSides] = useState<{ a: string; b: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || sides || loading) return;
    let live = true;
    setLoading(true);
    setError(null);
    api
      .compareVersions(pageId, fromVersionId, toVersionId)
      .then((res) => {
        if (!live) return;
        setSides({
          a: res.version_a?.content || '',
          b: res.version_b?.content || '',
        });
      })
      .catch((e) => {
        if (live) setError(e instanceof Error ? e.message : 'Failed to load diff');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pageId, fromVersionId, toVersionId]);

  const files = useMemo(() => {
    if (!sides) return [];
    if (sides.a === sides.b) return [];
    try {
      const text = createGitDiff(sides.a, sides.b);
      if (!text) return [];
      return parseDiff(text).filter((f) => f.hunks && f.hunks.length > 0);
    } catch (e) {
      console.error('Failed to parse diff', e);
      return [];
    }
  }, [sides]);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:underline"
        >
          {open ? 'Hide changes' : label || 'See what changed'}
        </button>
        {/* Revert lives in the settings Versions tab, which is where the full
            history + compare + delete already are. Linking rather than
            duplicating it keeps one place that owns version management. */}
        {/* `section` is a ROUTE segment, not a query param (see App.tsx) — a
            ?section= link silently lands on General. */}
        <Link
          to={withReturnTo(
            `/page-builder/${pageId}/settings/versions`,
            `/page-builder/${pageId}`,
            'the page builder',
          )}
          className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
        >
          Version history &amp; revert →
        </Link>
      </div>
      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-2 py-2 overflow-x-auto text-xs">
          {loading && (
            <div className="text-gray-500 dark:text-gray-400">Loading diff…</div>
          )}
          {error && <div className="text-rose-600 dark:text-rose-400">{error}</div>}
          {!loading && !error && files.length === 0 && (
            <div className="text-gray-500 dark:text-gray-400 italic">
              No textual difference between these versions.
            </div>
          )}
          {files.map((file, i) => (
            <Diff
              key={i}
              viewType="unified"
              diffType={file.type}
              hunks={file.hunks || []}
              gutterType="none"
            >
              {(hunks) => hunks.map((hunk) => <Hunk key={hunk.content} hunk={hunk} />)}
            </Diff>
          ))}
        </div>
      )}
    </div>
  );
}
